import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';

import { Store } from '@ngxs/store';
import { Subscription, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize } from 'rxjs/operators';
import { debounceTime } from 'rxjs/operators';

import { eventImage, orderBy } from '../../core/helpers';
import { EventService, ToastService, ValidationService } from '../../core/services';

@Component({
    selector: 'gs-search',
    templateUrl: './search.component.html',
    standalone: false
})
export class SearchComponent implements OnInit, OnDestroy {
  events: any[];
  eventTypes: any[] = [];
  sort: string = "newest";
  activeFiltersCollapsed: boolean = false;

  form: FormGroup = this.formBuilder.group({
    keyword: ['', []],
    active: [true, []],
    completed: [true, []],
    eventTypes: this.formBuilder.array([])
  });

  private subscriptions: Subscription[] = [];

  get controls() {
    return this.form.controls;
  }

  get eventTypesArray() {
    return this.form.controls['eventTypes'] as FormArray
  }

  get keywordFilter() {
    return this.form.value.keyword;
  }

  get activeFilter() {
    return this.form.value.active;
  }

  get completedFilter() {
    return this.form.value.completed;
  }

  get eventTypesFilter() {
    return this.form.value.eventTypes.map((checked, index) => checked ? this.eventTypes[index] : null).filter(x => x != null);
  }

  get hasFilters() {
    return this.keywordFilter || this.activeFilter || this.completedFilter || this.eventTypesFilter?.length;
  }

  constructor(
    private eventService: EventService,
    private toastService: ToastService,
    private store: Store,
    private offcanvasService: NgbOffcanvas,
    private formBuilder: FormBuilder,
    public vs: ValidationService
  ) {
  }

  ngOnInit() {
    this.searchEvents();
    this.getEventTypeList();

    this.subscriptions.push(
      this.form.valueChanges
        .pipe(
          debounceTime(1000),
          distinctUntilChanged()
        )
        .subscribe(x => {
          this.searchEvents(
            !x.keyword || x.keyword.length < 3 ? '' : x.keyword,
            x.active == x.completed ? '' : x.active ? 'Active' : 'Completed',
            x.eventTypes.map((checked, index) => checked ? this.eventTypes[index].id : null).filter(x => x != null)
          );
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  getEventTypeList() {
    this.subscriptions.push(
      this.store.select<any>(state => state.staticData.eventTypes)
        .pipe(filter(x => !!x))
        .subscribe((response: any) => {
          this.eventTypes = response.map(x => ({ ...x }));
          this.eventTypesArray.clear();
          // this.eventTypesArray.push(this.formBuilder.control(true, []));
          this.eventTypes.forEach(() => {
            this.eventTypesArray.push(this.formBuilder.control(null, []));
          });
        })
    );
  }

  onOpenFilterMenuClick(template: TemplateRef<any>) {
    this.offcanvasService.open(template, {panelClass: 'offcanvas-lg', position: 'start'});
  }

  onSortChange() {
    this.sortEvents(this.events, this.sort)
  }

  onRemoveFilterClick(filter: any) {
    this.form.patchValue({
      ...filter
    });
  }

  onRemoveAllFiltersClick() {
    this.form.reset({
      keyword: '',
      active: true,
      completed: true,
      eventTypes: this.eventTypes.map(() => false)
    });
  }

  onRemoveEventTypeFilterClick(eventTypeId: number) {
    const eventTypeIndex = this.eventTypes.findIndex(x => x.id == eventTypeId);
    this.onRemoveFilterClick({
      eventTypes: this.form.value.eventTypes.map((checked, index) => index === eventTypeIndex ? false : checked)
    });
  }

  /**
   * Sort items based on selection
   *
   * @private
   * @param {*} list
   * @param {string} sort
   * @returns
   * @memberof GiftCardsListComponent
   */
  private sortEvents(list: any[], sort: string) {
    switch (sort) {
      case 'oldest': {
        return orderBy(list, 'createdDate');
      }
      case 'deadline': {
        return orderBy(list, '-deadlineDate');
      }
      case 'low-high': {
        return orderBy(list, 'totalContributionAmount');
      }
      case 'high-low': {
        return orderBy(list, '-totalContributionAmount');
      }
      default: {
        return orderBy(list, '-createdDate');
      }
    }
  }

  private searchEvents(keyword: string = null, status: string = null, eventTypes: number[] = null) {
    this.events = null;
    this.subscriptions.push(
      this.eventService.searchEvents(keyword, status, eventTypes)
        .pipe(
          catchError(e => {
            this.toastService.checkErrorForStatus(e);
            return throwError(e);
          }),
          finalize(() => { })
        )
        .subscribe((response: any) => {

          response.forEach((x) => {
            x.image = eventImage(x);
          });

          this.events = [...response];
        })
    );
  }

}
